import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { ServiceItemForm } from "@/components/admin/commercial/service-item-form";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ListChecks, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ServiceItemRow {
  id: string;
  name: string;
  billingMode: string;
  isRecurringRevenue: boolean;
  requiresTask: boolean;
  isActive: boolean;
  budget: { id: string; name: string };
  client: { name: string };
  serviceType: { id: string; label: string };
}

export default function ServiceItemsPage() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceItemRow | null>(null);

  const { data, isLoading } = useQuery<ServiceItemRow[]>({
    queryKey: ["/api/service-items"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const response = await apiRequest("POST", "/api/service-items", payload);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/service-items"] });
      setDialogOpen(false);
      toast({ title: "Service item created" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Record<string, unknown> }) => {
      const response = await apiRequest("PATCH", `/api/service-items/${id}`, payload);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/service-items"] });
      setDialogOpen(false);
      setEditing(null);
      toast({ title: "Service item updated" });
    },
  });

  const columns: ColumnDef<ServiceItemRow>[] = [
    {
      key: "name",
      header: "Service item",
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.name}</span>
          <span className="text-xs text-muted-foreground">
            {row.client.name} · {row.budget.name}
          </span>
        </div>
      ),
    },
    {
      key: "serviceType",
      header: "Type",
      cell: (row) => row.serviceType.label,
    },
    {
      key: "billingMode",
      header: "Billing mode",
      cell: (row) => row.billingMode.replace(/_/g, " "),
    },
    {
      key: "flags",
      header: "Flags",
      cell: (row) =>
        [
          row.isRecurringRevenue ? "Recurring" : null,
          row.requiresTask ? "Requires task" : null,
          row.isActive ? "Active" : "Inactive",
        ]
          .filter(Boolean)
          .join(" · "),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Service Items"
        description="Billable delivery items underneath budgets"
        action={
          <Button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Service Item
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        onRowClick={(row) => {
          setEditing(row);
          setDialogOpen(true);
        }}
        emptyIcon={ListChecks}
        emptyTitle="No service items"
        emptyDescription="Service items connect budgets to actual billable delivery."
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Service Item" : "Create Service Item"}</DialogTitle>
          </DialogHeader>
          <ServiceItemForm
            defaultValues={
              editing
                ? {
                    budgetId: editing.budget.id,
                    serviceTypeId: editing.serviceType.id,
                    name: editing.name,
                    billingMode: editing.billingMode as any,
                    isRecurringRevenue: editing.isRecurringRevenue,
                    requiresTask: editing.requiresTask,
                    isActive: editing.isActive,
                    quantity: "1",
                  }
                : undefined
            }
            onSubmit={(payload) => {
              if (editing) {
                updateMutation.mutate({ id: editing.id, payload });
                return;
              }
              createMutation.mutate(payload);
            }}
            isPending={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
